import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import useTableSort from "components/Table/useTableSort";
import useErrorHandle from "hooks/useErrorHandle";
import {
  AuthorizationScheme,
  ManagedUserGroupIntegration,
  SearchQueryOrderDirection,
} from "types/generated";

import ManageSlackAccessPageLayout from "./components/PageLayout";
import SlackTable from "./components/Table";
import {
  SLACK_COLUMN_ID,
  SLACK_TABLE_FILTERS_STORAGE_KEY,
  SORT_CONFIG,
} from "./components/Table/constants";
import ManageSlackAccessContext from "./context";
import { GET_MANAGED_USER_GROUP_INTEGRATIONS } from "./gql";
import { UserManagementActivationStatus } from "./types";
import { useFilteredIntegrations } from "./useFilteredIntegrations";

type GetManagedUserGroupIntegrationsGql = {
  authorizationScheme: AuthorizationScheme;
  managedUserGroupIntegrations: ManagedUserGroupIntegration[];
};

const ManageSlackAccess = () => {
  const { loading, data, error } = useQuery<GetManagedUserGroupIntegrationsGql>(
    GET_MANAGED_USER_GROUP_INTEGRATIONS
  );

  const activationStatus =
    data?.authorizationScheme === AuthorizationScheme.ManagedUsers
      ? UserManagementActivationStatus.ACTIVE
      : UserManagementActivationStatus.INACTIVE;

  const { sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: SLACK_TABLE_FILTERS_STORAGE_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption: SORT_CONFIG[SLACK_COLUMN_ID.NAME],
    initialSortDirection: SearchQueryOrderDirection.Asc,
  });

  const filteredIntegrations = useFilteredIntegrations(
    data?.managedUserGroupIntegrations ?? [],
    sortDescriptor
  );

  const contextValue = useMemo(() => ({ activationStatus }), [activationStatus]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.managedUserGroupIntegrations) {
    return <PageLoading />;
  }

  if (!data?.managedUserGroupIntegrations) {
    return <NotFoundPage />;
  }

  return (
    <ManageSlackAccessContext.Provider value={contextValue}>
      <ManageSlackAccessPageLayout activationStatus={activationStatus}>
        <SlackTable integrations={filteredIntegrations} />
      </ManageSlackAccessPageLayout>
    </ManageSlackAccessContext.Provider>
  );
};

export default ManageSlackAccess;
